import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  completeEmailSignIn,
  isEmailSignInLink,
  getStoredEmailForSignIn,
  authService,
  useAuth,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '@aaas/shared';

type Status = 'loading' | 'needs-email' | 'success' | 'error';

export function EmailCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '/';
  const { user } = useAuth();
  const { t } = useTranslation('portal');
  const [status, setStatus] = useState<Status>('loading');
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      // Already authenticated (Firebase auto-signed in from email link)
      navigate(redirectTo, { replace: true });
      return;
    }
    handleEmailSignIn();
  }, [user]);

  const handleEmailSignIn = async (providedEmail?: string) => {
    // Check if this is a valid email sign-in link
    if (!isEmailSignInLink()) {
      setStatus('error');
      setError(t('emailCallback.invalidLink'));
      return;
    }

    // Get email from localStorage or parameter
    const signInEmail = providedEmail || getStoredEmailForSignIn();

    if (!signInEmail) {
      // User opened link on different device, need to ask for email
      setStatus('needs-email');
      return;
    }

    try {
      const result = await completeEmailSignIn(undefined, signInEmail);
      const idToken = await result.user.getIdToken();

      // Verify token with backend
      await authService.verifyToken(idToken);

      setStatus('success');

      setTimeout(() => {
        navigate(redirectTo, { replace: true });
      }, 1500);
    } catch (err) {
      console.error('Email sign-in failed:', err);
      setStatus('error');
      setError(err instanceof Error ? err.message : t('emailCallback.signInFailed'));
    }
  };

  const handleSubmitEmail = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !email.includes('@')) {
      setError(t('emailCallback.invalidEmail'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    await handleEmailSignIn(email);
    setIsSubmitting(false);
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-center">
              {status === 'loading' && t('emailCallback.signingIn')}
              {status === 'needs-email' && t('emailCallback.confirmEmail')}
              {status === 'success' && t('emailCallback.signInSuccess')}
              {status === 'error' && t('emailCallback.signInFailed')}
            </CardTitle>
            {status === 'needs-email' && (
              <CardDescription className="text-center">
                {t('emailCallback.confirmDescription')}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {status === 'loading' && (
              <div className="flex flex-col items-center justify-center py-8">
                <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
                <p className="mt-4 text-gray-600">{t('emailCallback.pleaseWait')}</p>
              </div>
            )}

            {status === 'needs-email' && (
              <form onSubmit={handleSubmitEmail} className="space-y-4">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                    {error}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email">{t('emailCallback.emailLabel')}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder={t('emailCallback.emailPlaceholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isSubmitting}
                    required
                  />
                </div>

                <Button type="submit" disabled={isSubmitting} className="w-full">
                  <Mail className="w-4 h-4 mr-2" />
                  {isSubmitting ? t('emailCallback.completingButton') : t('emailCallback.completeButton')}
                </Button>

                <Button
                  type="button"
                  onClick={handleBackToLogin}
                  variant="ghost"
                  className="w-full"
                >
                  {t('emailCallback.backToLogin')}
                </Button>
              </form>
            )}

            {status === 'success' && (
              <div className="flex flex-col items-center justify-center py-8">
                <CheckCircle className="h-12 w-12 text-green-600" />
                <p className="mt-4 text-gray-600">{t('emailCallback.redirecting')}</p>
              </div>
            )}

            {status === 'error' && (
              <div className="flex flex-col items-center justify-center py-8 space-y-4">
                <XCircle className="h-12 w-12 text-red-600" />
                <p className="text-red-600 text-center">{error}</p>
                <Button onClick={handleBackToLogin} variant="outline">
                  {t('emailCallback.backToLogin')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
